import './CrystalPlate.css'

import { SAMPLE_PLATE_LETTERS, SAMPLE_PLATE_NUMBERS } from '../../../../../constants/saxs'

import PropTypes from 'prop-types'
import React from 'react'
import find from 'lodash/find'

const gridMarginRight = 10 // in px
const gridMarginLeft = 30 // in px
const gridMarginTop = 20 // in px
const gridMarginBottom = 5 // in px
const ROUNDED_WELL = 'round'
const SQUARED_WELL = 'square'

/**
 * React component which renders a plate. (SVG technology based)
 */
class Plate extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      SVGRoot_width: 0,
      SVGRoot_height: 0,
    }

    this.SVGRoot = null

    this.createCircle = this.createCircle.bind(this)
    this.createSquare = this.createSquare.bind(this)
    this.arePropsValid = this.arePropsValid.bind(this)
    this.getCellWidth = this.getCellWidth.bind(this)
    this.getWells = this.getWells.bind(this)
    this.getPlate = this.getPlate.bind(this)
    this.getLabels = this.getLabels.bind(this)
    this.getSVGRootHeight = this.getSVGRootHeight.bind(this)
    this.setSVGRef = this.setSVGRef.bind(this)
    this.onWindowResized = this.onWindowResized.bind(this)

    window.addEventListener('resize', this.onWindowResized)
  }

  render() {
    return (
      <svg style={{ width: '100%', height: this.state.SVGRoot_height, fontSize: 'auto' }} ref={this.setSVGRef}>
        {this.SVGRoot && this.arePropsValid() === true ? this.getPlate() : null}
      </svg>
    )
  }

  componentDidMount() {
    this.setState({
      SVGRoot_width: this.SVGRoot.clientWidth,
      SVGRoot_height: this.getSVGRootHeight(this.SVGRoot.clientWidth),
    })
  }

  componentDidUpdate() {
    if (this.SVGRoot.clientWidth !== this.state.SVGRoot_width) {
      this.setState({
        SVGRoot_width: this.SVGRoot.clientWidth,
        SVGRoot_height: this.getSVGRootHeight(this.SVGRoot.clientWidth),
      })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResized)
  }

  setSVGRef(SVGElement) {
    this.SVGRoot = SVGElement
  }

  /** Function triggered when the window is resized. */
  onWindowResized() {
    this.setState({ SVGRoot_width: 0 }) // the goal is to trigger a new render.
  }

  getPlate() {
    const elements = []
    elements.push(this.getWells())
    elements.push(this.getLabels())

    return elements
  }

  /**
   * Renders the wells of the plate
   */
  getWells() {
    const _this = this

    const getWellSize = (col, cellWidth) => {
      if (_this.props.wellSize) {
        return (_this.props.wellSize[col] / 100) * cellWidth
      }
      return 0.5 * cellWidth
    }

    const getWellColor = (row, col) => {
      if (_this.props.filledWells && _this.props.filledWells instanceof Array) {
        const foundWell = find(_this.props.filledWells, well => well.row === row && well.col === col)
        if (foundWell) {
          return foundWell.color
        }
      }
      return '#FFFFFF'
    }

    const getWellStrokeColor = (row, col) => {
      if (_this.props.filledWells && _this.props.filledWells instanceof Array) {
        const foundWell = find(_this.props.filledWells, well => well.row === row && well.col === col)
        if (foundWell) {
          return foundWell.strokeColor
        }
      }
      return '#888888'
    }

    const cellWidth = this.getCellWidth()

    const wells = []
    for (let row = 0; row < this.props.rowLabels.length; row++) {
      for (let col = 0; col < this.props.colLabels.length; col++) {
        if (this.props.wellShape) {
          if (this.props.wellShape === ROUNDED_WELL) {
            wells.push(
              this.createCircle(
                'well_' + row + '_' + col,
                gridMarginLeft + cellWidth / 2 + col * cellWidth, // x
                gridMarginTop + cellWidth / 2 + row * cellWidth, // y
                getWellColor(SAMPLE_PLATE_LETTERS[row], SAMPLE_PLATE_NUMBERS[col]), // color
                getWellStrokeColor(SAMPLE_PLATE_LETTERS[row], SAMPLE_PLATE_NUMBERS[col]),
                getWellSize(col, cellWidth)
              )
            )
          }

          if (this.props.wellShape === SQUARED_WELL) {
            wells.push(
              this.createSquare(
                'well_' + row + '_' + col,
                gridMarginLeft + cellWidth / 4 + col * cellWidth, // x
                gridMarginTop + cellWidth / 4 + row * cellWidth, // y
                getWellColor(SAMPLE_PLATE_LETTERS[row], SAMPLE_PLATE_NUMBERS[col]), // color
                getWellStrokeColor(SAMPLE_PLATE_LETTERS[row], SAMPLE_PLATE_NUMBERS[col]),
                getWellSize(col, cellWidth)
              )
            )
          }
        } else {
          wells.push(
            this.createCircle(
              'well_' + row + '_' + col,
              gridMarginLeft + cellWidth / 2 + col * cellWidth, // x
              gridMarginTop + cellWidth / 2 + row * cellWidth, // y
              getWellColor(SAMPLE_PLATE_LETTERS[row], SAMPLE_PLATE_NUMBERS[col]), // color
              getWellStrokeColor(SAMPLE_PLATE_LETTERS[row], SAMPLE_PLATE_NUMBERS[col]),
              getWellSize(col, cellWidth)
            )
          )
        }
      }
    }

    return wells
  }

  /**
   * Compute the height of the SVG root element.
   * @param {*} SVGRoot_width current width of the SVG root element
   */
  getSVGRootHeight(SVGRoot_width) {
    return this.props.rowLabels.length * this.getCellWidth(SVGRoot_width) + gridMarginTop + gridMarginBottom
  }

  /** Get the width of a cell in the grid. It is also the height of the cell. */
  getCellWidth(SVGRoot_width) {
    let gridWidth = 0
    if (SVGRoot_width) {
      gridWidth = SVGRoot_width - gridMarginLeft - gridMarginRight
    } else {
      gridWidth = this.state.SVGRoot_width - gridMarginLeft - gridMarginRight
    }

    const cellWidth = Math.round(gridWidth / this.props.colLabels.length)
    return cellWidth
  }

  /**
   * Props validator
   * @returns {boolean} whether the provided props are valid or not.
   */
  arePropsValid() {
    const { rowLabels, colLabels, wellShape } = this.props

    if (rowLabels && rowLabels instanceof Array && rowLabels.length > 0) {
      if (colLabels && colLabels instanceof Array && colLabels.length > 0) {
        if (wellShape) {
          if (wellShape === ROUNDED_WELL || wellShape === SQUARED_WELL) {
            if (this.props.wellSize) {
              return !!(
                this.props.wellSize instanceof Array && this.props.wellSize.length === this.props.colLabels.length
              )
            }
            return true
          }
        } else {
          return true
        }
      }
    }

    return false
  }

  /**
   * Create a circle representing a well
   * @param {*} id
   * @param {*} x position of the center of the circle on X axis
   * @param {*} y position of the center of the circle on Y axis
   * @param {*} color inner color of the circle
   * @param {*} strokeColor color of the circle stroke
   * @param {*} wellWidth width of the well
   */
  createCircle(id, x, y, color, strokeColor, wellWidth) {
    return <circle key={id} id={id} cx={x} cy={y} r={wellWidth / 2} fill={color} strokeWidth="2" stroke={strokeColor} />
  }

  /**
   * Create a square representing a well
   * @param {*} id
   * @param {*} x position of the top left corner of the square on X axis
   * @param {*} y position of the top left corner of the square on Y axis
   * @param {*} color inner color of the square
   * @param {*} strokeColor color of the square stroke
   * @param {*} wellWidth width of the well
   */
  createSquare(id, x, y, color, strokeColor, wellWidth) {
    return (
      <rect
        key={id}
        id={id}
        x={x}
        y={y}
        width={wellWidth}
        height={wellWidth}
        fill={color}
        strokeWidth="2"
        stroke={strokeColor}
      />
    )
  }

  /**
   * Render the row and column labels of the plate
   */
  getLabels() {
    const labels = []

    // Display column labels
    for (let col = 0; col < this.props.colLabels.length; col++) {
      if (col < 9) {
        labels.push(
          <text
            key={this.props.colLabels[col]}
            x={gridMarginLeft + +this.getCellWidth() / 2 - 4 + col * this.getCellWidth()}
            y="17"
          >
            {this.props.colLabels[col]}
          </text>
        )
      } else {
        labels.push(
          <text
            key={this.props.colLabels[col]}
            x={gridMarginLeft + +this.getCellWidth() / 2 - 8 + col * this.getCellWidth()}
            y="17"
          >
            {this.props.colLabels[col]}
          </text>
        )
      }
    }

    // Display row labels
    for (let row = 0; row < this.props.rowLabels.length; row++) {
      labels.push(
        <text
          key={this.props.rowLabels[row]}
          x="10"
          y={gridMarginTop + this.getCellWidth() / 2 + 4 + row * this.getCellWidth()}
        >
          {this.props.rowLabels[row]}
        </text>
      )
    }

    return labels
  }
}

Plate.propTypes = {
  /* List of columns in the plate. List items correspond to the column label. Example  ['1', '2', '3'] renders a 3 columns grid which labels are '1', '2', '3' */
  colLabels: PropTypes.array.isRequired,
  /* List of fille wells of the plate.
    Structure: filledWells={[ { row: 'B', col: 2, color: '#000011' }, { row: 'C', col: 4, color: '#554477' } ]} */
  filledWells: PropTypes.array,
  /* List of rows in the plate. List items correspond to the row label. Example  ['A', 'B', 'C'] render a 3-row grid which labels are 'A', 'B', 'C' */
  rowLabels: PropTypes.array.isRequired,
  /* Shape of the well. Currently supported: 'round', 'square' */
  wellShape: PropTypes.string,
  /* Size of the wells. Example for a 3 column plate: wellSize= [ 25, 40, 100] defines that well size for col 1 is 25%, wellsize for col 2 is 40% and well Size for col 3 is 100%.
    The percentage refers to the cell hosting the well not to the plate. wellShape array must be the same length as colTitle. */
  wellSize: PropTypes.array,
}

export default Plate
